import { useEffect, useState, useCallback, useMemo } from "react";
import { Tag, Pencil, Trash2, ChevronDown, ChevronUp, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

/**
 * Centralised Tag Management (2026-02 per user spec 「产品下建立 tag 清单」).
 *
 * Displays every distinct Product.tag currently in use across the catalog with
 * usage counts, plus bulk-rename and bulk-delete controls that cascade across
 * every product carrying that tag. Products themselves are never removed —
 * delete only clears the ``tag`` field (spec 3a).
 *
 * Rename collision handling (spec 4c): if the target name already exists, we
 * surface a confirm prompt asking the user whether to MERGE the two tags.
 */
export function TagManagerDialog({ open, onOpenChange, onChanged }) {
  const { t } = useLanguage();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true); // spec 2a: single global expand/collapse
  const [filter, setFilter] = useState("");
  const [renaming, setRenaming] = useState(null); // { old_name, new_name }
  const [deleting, setDeleting] = useState(null); // { name, count }
  const [mergePrompt, setMergePrompt] = useState(null); // { old_name, new_name, collision_count }
  const [busy, setBusy] = useState(false);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products/tags/all");
      setTags(data.tags || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setFilter("");
      setRenaming(null);
      setDeleting(null);
      setMergePrompt(null);
      fetchTags();
    }
  }, [open, fetchTags]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((tg) => tg.name.toLowerCase().includes(q));
  }, [tags, filter]);

  const performRename = async (mergeIfExists = false) => {
    if (!renaming) return;
    const { old_name } = renaming;
    const new_name = (renaming.new_name || "").trim();
    if (!new_name) {
      toast.error(t("products.tagRenameNew"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/products/tags/rename", {
        old_name,
        new_name,
        merge_if_exists: mergeIfExists,
      });
      toast.success(mergeIfExists ? t("products.tagMerged") : t("products.tagRenamed"));
      setRenaming(null);
      setMergePrompt(null);
      await fetchTags();
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // spec 4c: on collision, prompt the user whether to merge
      if (e.response?.status === 409 && detail?.code === "tag_exists") {
        setMergePrompt({
          old_name,
          new_name,
          collision_count: detail.collision_count || 0,
        });
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const performDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete("/products/tags/by-name", { params: { name: deleting.name } });
      toast.success(t("products.tagDeleted"));
      setDeleting(null);
      await fetchTags();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="tag-manager-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Tag size={16} /> {t("products.tagListTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {t("products.tagListDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t("products.tagListSearch")}
                data-testid="tag-manager-search-input"
                className="pl-9 rounded-md h-9"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setExpanded((v) => !v)}
              data-testid="tag-manager-toggle-expand"
              className="rounded-md h-9"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {expanded ? t("products.tagListCollapse") : t("products.tagListExpand")}
            </Button>
          </div>

          {expanded && (
            <div className="max-h-[55vh] overflow-y-auto rounded-md border border-border" data-testid="tag-manager-list">
              {loading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">...</div>
              ) : filtered.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground" data-testid="tag-manager-empty">
                  {t("products.tagListEmpty")}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {filtered.map((tg) => (
                    <li
                      key={tg.name}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50"
                      data-testid={`tag-row-${tg.name}`}
                    >
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 text-brand px-2.5 py-0.5 text-xs font-semibold">
                        <Tag size={12} /> {tg.name}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {tg.count} {t("products.tagListCount")}
                      </span>
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 rounded-md"
                          onClick={() => setRenaming({ old_name: tg.name, new_name: tg.name })}
                          data-testid={`tag-rename-${tg.name}`}
                        >
                          <Pencil size={13} /> {t("products.tagRename")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 rounded-md text-destructive hover:text-destructive"
                          onClick={() => setDeleting({ name: tg.name, count: tg.count })}
                          data-testid={`tag-delete-${tg.name}`}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(v) => !v && setRenaming(null)}>
        <DialogContent className="max-w-sm" data-testid="tag-rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">{t("products.tagRenameTitle")}</DialogTitle>
            <DialogDescription className="text-xs">
              {renaming?.old_name}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renaming?.new_name || ""}
            onChange={(e) => setRenaming((s) => ({ ...s, new_name: e.target.value }))}
            placeholder={t("products.tagRenameNew")}
            data-testid="tag-rename-input"
            onKeyDown={(e) => e.key === "Enter" && performRename(false)}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)} className="rounded-md">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => performRename(false)}
              disabled={busy}
              className="rounded-md bg-brand hover:bg-brand-hover"
              data-testid="tag-rename-confirm"
            >
              {busy ? "..." : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge-on-collision confirm dialog (spec 4c) */}
      <Dialog open={!!mergePrompt} onOpenChange={(v) => !v && setMergePrompt(null)}>
        <DialogContent className="max-w-sm" data-testid="tag-merge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">{t("products.tagMergeTitle")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("products.tagMergeDesc")
                .replace("{name}", mergePrompt?.new_name || "")
                .replace("{count}", String(mergePrompt?.collision_count ?? 0))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergePrompt(null)} className="rounded-md">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => performRename(true)}
              disabled={busy}
              className="rounded-md bg-brand hover:bg-brand-hover"
              data-testid="tag-merge-confirm"
            >
              {busy ? "..." : t("products.tagMerge")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog (spec 3a + 3b) */}
      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="max-w-sm" data-testid="tag-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">{t("products.tagDeleteTitle")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("products.tagDeleteDesc")
                .replace("{name}", deleting?.name || "")
                .replace("{count}", String(deleting?.count ?? 0))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} className="rounded-md">
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={performDelete}
              disabled={busy}
              className="rounded-md"
              data-testid="tag-delete-confirm"
            >
              {busy ? "..." : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
